import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { TrendingUp, TrendingDown, Minus, History } from "lucide-react";

/**
 * Shared retake-delta UI. Fetches a framework's history endpoint, finds the
 * most recent COMPLETED attempt *prior to* the current one, and renders:
 *   - `<HistoryDelta.Headline />`     — big delta chip next to the headline score
 *   - `<HistoryDelta.Inline />`       — small delta chip for per-category rows
 *
 * Props:
 *   historyUrl   — GET endpoint returning `{ items: [enriched_doc, ...] }` newest-first
 *   currentId    — id of the current attempt (so we can find "the one before")
 *   direction    — "maturity" (↑ = good, green) or "risk" (↑ = bad, red). Default "maturity".
 *
 * The headline component yields the previous score + delta number; the inline
 * variant is a controlled child used inside breakdown rows once you have the
 * previous category map from `HistoryDelta.useHistory()`.
 */

export function useHistory(historyUrl, currentId) {
  const [items, setItems] = useState(null);
  useEffect(() => {
    if (!historyUrl) return;
    let mounted = true;
    api.get(historyUrl).then(({ data }) => {
      if (!mounted) return;
      setItems(data.items || []);
    }).catch(() => mounted && setItems([]));
    return () => { mounted = false; };
  }, [historyUrl]);

  if (!items) return { loading: true, attempts: 0, previous: null };
  const completed = items.filter((i) => i.status === "completed" || i.status === "archived");
  // Find current index (if currentId provided) — otherwise assume index 0 is current.
  let curIdx = 0;
  if (currentId) {
    const idx = completed.findIndex((i) => i.id === currentId);
    if (idx >= 0) curIdx = idx;
  }
  const previous = completed[curIdx + 1] || null; // the one BEFORE the current
  return { loading: false, attempts: completed.length, previous, items: completed };
}

function deltaColorClasses(delta, direction) {
  if (delta === 0) return "text-ink-muted bg-secondary border-ink-line";
  const isGood = direction === "risk" ? delta < 0 : delta > 0;
  return isGood
    ? "text-good bg-good-soft border-good/30"
    : "text-warn bg-warn-soft border-warn/30";
}

function DeltaIcon({ delta, size = "w-3 h-3" }) {
  if (delta === 0) return <Minus className={size} />;
  return delta > 0 ? <TrendingUp className={size} /> : <TrendingDown className={size} />;
}

function fmtSigned(n, decimals = 0) {
  const v = Number(n) || 0;
  const rounded = decimals > 0 ? v.toFixed(decimals) : Math.round(v);
  if (v === 0) return "0";
  return v > 0 ? `+${rounded}` : `${rounded}`;
}

/** Headline delta — sits next to the big result score on the results screen.
 *  Pass `historyData` from the parent's `useHistory()` call to avoid a duplicate fetch. */
export function HistoryDeltaHeadline({ historyUrl, currentId, currentScore, direction = "maturity", testid, historyData }) {
  // Fallback: fetch our own history if parent did not pass it down.
  const own = useHistory(historyData ? null : historyUrl, currentId);
  const { loading, attempts, previous } = historyData || own;
  if (loading || attempts <= 1 || !previous) return null;
  const prevScore = Number(previous.total_score) || 0;
  const cur = Number(currentScore) || 0;
  const delta = cur - prevScore;
  const cls = deltaColorClasses(delta, direction);
  return (
    <div className="flex items-center gap-2 mt-4" data-testid={testid || "history-delta-headline"}>
      <div className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm border text-sm font-num font-bold ${cls}`}>
        <DeltaIcon delta={delta} size="w-4 h-4" />
        {fmtSigned(delta, prevScore % 1 || cur % 1 ? 1 : 0)}
      </div>
      <div className="text-[11px] uppercase tracking-wider text-ink-muted leading-tight">
        <div>vs previous · <span className="font-num text-ink font-semibold">{prevScore}</span></div>
        <div className="opacity-70">Attempt {attempts}{attempts > 1 ? ` of ${attempts}` : ""}</div>
      </div>
    </div>
  );
}

/** Small inline delta chip — sits next to a per-category percentage on the breakdown. */
export function CategoryDelta({ prevScore, prevMax, curScore, curMax, direction = "maturity", testid }) {
  if (prevScore == null || prevMax == null) return null;
  const prevPct = prevMax > 0 ? (prevScore / prevMax) * 100 : 0;
  const curPct  = curMax  > 0 ? (curScore  / curMax)  * 100 : 0;
  const delta = Math.round(curPct - prevPct);
  if (delta === 0) {
    return (
      <span data-testid={testid} className="inline-flex items-center gap-0.5 text-[10px] font-num text-ink-muted px-1 py-0.5 border border-ink-line rounded-sm" title={`No change from ${Math.round(prevPct)}%`}>
        <Minus className="w-2.5 h-2.5" /> 0
      </span>
    );
  }
  const cls = deltaColorClasses(delta, direction);
  return (
    <span data-testid={testid} className={`inline-flex items-center gap-0.5 text-[10px] font-num font-bold border rounded-sm px-1 py-0.5 ${cls}`}
      title={`Previous: ${Math.round(prevPct)}% → Current: ${Math.round(curPct)}%`}>
      <DeltaIcon delta={delta} size="w-2.5 h-2.5" />{fmtSigned(delta)}
    </span>
  );
}

/** Optional attempts indicator — a small chip showing total attempts. */
export function AttemptsChip({ historyUrl, currentId, testid }) {
  const { attempts } = useHistory(historyUrl, currentId);
  if (attempts <= 1) return null;
  return (
    <span data-testid={testid || "attempts-chip"} className="chip border-ink-line bg-secondary text-ink-muted text-[10px] font-num inline-flex items-center gap-1">
      <History className="w-3 h-3" />Attempt {attempts}
    </span>
  );
}

/** Build a code→{score,max} map from a previous assessment's category_breakdown. */
export function buildPrevCategoryMap(previous, key = "id") {
  if (!previous) return {};
  const list = previous.category_breakdown || previous.categories || [];
  const map = {};
  list.forEach((c) => {
    if (c[key] != null) map[c[key]] = { score: c.score, max: c.max_score };
  });
  return map;
}
